import Vue from 'vue';
import { BG_EXCHANGE_RATE } from '../consts/bg-exchange-rate';
import { formatAsPrice, normalizeModelName } from '../utils';

Vue.component('configurator-price-indicator', {
    props: {
        siteHandle: {
            type: String,
        },
        linkBuilder: {
            required: false,
        },
    },
    data() {
        return {
            indicatorEnabled: false,
            priceAvailable: true,
            directPaymentEnabled: false,
            priceText: '',
            comingSoonText: null,
            isPdfDownloading: false,
        };
    },

    mounted() {
        // Update price text and enable indicator if a new price is set.
        EventBus.$on(
            'configurator-price-update',
            (priceText, formattedPriceText) => {
                this.priceText = this.formatDoubleViewPrice(formattedPriceText);
                this.priceAvailable = true;
                this.indicatorEnabled = true;
            },
        );

        // Set price n/a and enable indicator if a new price is unavailable.
        EventBus.$on('configurator-price-na', () => {
            this.priceText = '';
            this.priceAvailable = false;
            this.indicatorEnabled = true;
        });

        EventBus.$on('configurator-price-coming-soon', (text) => {
            this.indicatorEnabled = true;
            this.comingSoonText = text;
        });

        // Disable price indicator if so required.
        EventBus.$on('configurator-price-disable', () => {
            if (this.comingSoonText) return;
            this.indicatorEnabled = false;
        });

        // Enable or disable buy bow button.
        EventBus.$on('configurator-set-payment-option', (paymentOption) => {
            this.directPaymentEnabled =
                paymentOption.field_paymentOption_paymentEnabled === '1';
        });
    },

    methods: {
        formatDoubleViewPrice(price) {
            if (this.siteHandle !== 'mgMotorsBulgaria') return price;

            const priceInNumbers = parseFloat(
                String(price).replace(' ', '').replace('€', '').replace('.', ''),
            );
            const priceInBg = priceInNumbers * BG_EXCHANGE_RATE;

            // "price" variable is something that comes directly from CMS and it already has the € symbol
            return `${price} / ${formatAsPrice(priceInBg)} лв`;
        },

        shouldDisableButton() {
            return Boolean(this.comingSoonText);
        },

        shouldShowScfPriceBlock() {
            return this.$parent.scfHandle !== 'multi';
        },

        completeConfigurator() {
            this.$parent.completeConfigurator();

            if (this.$parent.siteHandle == 'mgMotorsFrance') {
                const dodDescription = `Lead ayant configuré un/une ${normalizeModelName(
                    this.$parent.model,
                )} sur le site mgmotor.fr.`;

                const calculatorResultForDod = {
                    isAgricoleCalculation: false,
                    dodDescription,
                };
                EventBus.$emit(
                    'agricole-calculator-finished',
                    calculatorResultForDod,
                );
                EventBus.$emit(
                    'toggle-agricole-calculator-footer-notes',
                    false,
                );
            }
        },

        async downloadPdf(event) {
            this.isPdfDownloading = true;
            if (
                !this.$parent.configuratorResult.version ||
                !this.$parent.configuratorResult.color
            ) {
                alert(
                    'Please complete the configuration before downloading the PDF.',
                );
                return;
            }

            const target = event?.currentTarget;
            const dataset = target?.dataset || {};

            const defaultInteriorHandle =
                this.$parent.model === 'mg4' ? 'black-interior' : 'interior';
            const selectedInterior = this.$parent.configuratorResult.options
                ? Object.values(this.$parent.configuratorResult.options).find(
                      (option) => option.selected,
                  )
                : null;

            const configurationPayload = {
                siteId: Number(this.$parent.siteId),
                model:
                    this.$parent.modelCommerceHandle ||
                    this.$parent.model ||
                    'N/A',
                modelName: normalizeModelName(this.$parent.model),
                edition: this.$parent.configuratorResult?.type?.name || 'N/A',
                editionHandle:
                    this.$parent.configuratorResult?.type?.key || 'N/A',
                engineType:
                    this.$parent.configuratorResult.version?.name || 'N/A',
                color: this.$parent.configuratorResult.color?.name || 'N/A',
                interior: selectedInterior?.name || 'N/A',
                interiorImageUrl: 'N/A',
                interiorHandle:
                    selectedInterior?.handle || defaultInteriorHandle,
                colorVATNotice:
                    this.$parent.configuratorResult.color &&
                    this.$parent.configuratorResult.color.metallic
                        ? dataset.colorNoticeMetallic ||
                          'Metallic color surcharge applies'
                        : dataset.colorNoticeDefault || 'No additional costs',
                deliveryNotice: [dataset.deliveryNotice || 'N/A'],
                driveType:
                    this.$parent.configuratorResult.version?.name || 'N/A',
                paymentMethod:
                    this.$parent.configuratorResult.price?.name || 'N/A',
                price: this.$parent.configuratorResult.priceIndication || '0',
                energyLabelsImageUrl: dataset.energyLabelUrl || 'N/A',
                priceDisclaimer: this.$parent.priceDisclaimer || 'N/A',
                modelImagePath: `/images/configurator/${this.$parent.modelCommerceHandle}/${this.$parent.configuratorResult.type.key}/${this.$parent.configuratorResult.color?.key}.png`,
                fullModelImageUrl: 'N/A',
                promotions:
                    Object.values(this.$parent.configuratorResult.promotions)
                        .length > 0
                        ? Object.values(
                              this.$parent.configuratorResult.promotions,
                          )
                        : [null],
                disclaimer: this.$parent.disclaimer || 'N/A',
                versionDisclaimer:
                    this.$parent.parseVersionDisclaimer(
                        this.$parent.versionDisclaimer,
                    ) || 'N/A',
                paymentDisclaimer:
                    this.$parent.configuratorResult.paymentDisclaimer &&
                    this.$parent.configuratorResult.paymentDisclaimer.length > 0
                        ? this.$parent.configuratorResult.paymentDisclaimer
                              .filter((disclaimer) => disclaimer)
                              .join(' ') || 'N/A'
                        : 'N/A',
            };

            const endpoint =
                '/actions/configurator-module/v1/configurator-api/create-configuration-pdf';

            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(configurationPayload),
                });

                if (!response.ok) {
                    throw new Error(
                        `Request failed with status ${response.status}`,
                    );
                }

                const blob = await response.blob();
                const filename = this.buildPdfFilename(configurationPayload);

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('downloadPdf failed', error);
                alert('Unable to generate PDF. Please try again later.');
            } finally {
                this.isPdfDownloading = false;
            }
        },

        buildPdfFilename(configuration) {
            const slugify = (value) =>
                (value || 'configuration')
                    .toString()
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');

            return `${slugify(configuration.model)}-${slugify(
                configuration.edition,
            )}.pdf`;
        },

        pdfDownloadingClass() {
            return this.isPdfDownloading ? 'pdf-button--loading' : '';
        },

        pdfDownloadingTextClass() {
            return this.isPdfDownloading
                ? 'pdf-button__text--hidden'
                : 'pdf-button__text--visible';
        },

        pdfDownloadingSpinnerClass() {
            return this.isPdfDownloading
                ? 'pdf-button__loading-spinner--visible'
                : 'pdf-button__loading-spinner--hidden';
        },
    },
});
